/* Os lancamentos por tras de um valor agregado.
 *
 * Existe porque ate agora o painel parava no total: dava para ver que MERCADO somou
 * R$ 5.941,20 em junho e nao dava para ver as sete linhas que formaram isso.
 *
 * A linha de reconciliacao no topo nao e enfeite. `entries` guarda o valor ja assinado
 * pelo estorno, igual a `series`, e `assert_entries_reconcile` prende os dois no build --
 * entao mostrar as duas somas lado a lado e o que torna o detalhe verificavel por quem
 * le, e nao so por quem escreveu. Se um dia divergirem, aparece em vermelho na tela em
 * vez de passar batido.
 */

import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, qs } from "../lib/api";
import type { EntriesPayload } from "../lib/api";
import { brl2, cell, fmtMes, shortName } from "../lib/format";
import { Card, ErrorState, Skeleton } from "./ui";

const PAGE_SIZE = 100;

export function EntryPanel({ base, params, title, sub, expected, csvHref, onClose,
                             showUser = false }: {
  /** Endpoint, sem paginação: `/api/users/:id/entries` ou `/api/categories/entries`. */
  base: string;
  params: Record<string, string | number | undefined>;
  title: string;
  sub?: string;
  /** Valor que a tabela mostrava. A soma dos lançamentos tem de bater com ele. */
  expected?: number | null;
  csvHref: string;
  onClose: () => void;
  showUser?: boolean;
}) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("date");
  const key = JSON.stringify(params);
  useEffect(() => { setPage(1); }, [key, sort]);

  const listing = useQuery({
    queryKey: ["entries", base, key, sort, page],
    queryFn: () => api<EntriesPayload>(
      `${base}${qs({ ...params, sort, page, page_size: PAGE_SIZE })}`),
    placeholderData: (previous) => previous,
  });

  const items = listing.data?.items ?? [];
  const sum = listing.data?.totals.sum ?? 0;
  const matches = expected === null || expected === undefined
    || Math.abs(sum - expected) < 0.005;

  return (
    <Card
      id="lancamentos"
      title={title}
      sub={sub}
      right={
        <>
          <label>ordenar
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="date">mais recente</option>
              <option value="amount">maior valor</option>
              <option value="description">descrição</option>
            </select>
          </label>
          <a className="btn" href={csvHref}>baixar CSV</a>
          <button className="btn" onClick={onClose}>fechar</button>
        </>
      }
    >
      {listing.data && (
        <p className={matches ? "reconcile" : "reconcile off"}>
          {matches ? "✓" : "✗"} soma dos lançamentos <strong>R$ {brl2(sum)}</strong>
          {expected !== null && expected !== undefined && (
            <> · valor da tabela <strong>R$ {brl2(expected)}</strong></>
          )}
          {" · "}{listing.data.totals.rows.toLocaleString("pt-BR")} lançamentos
          {listing.data.totals.months
            ? ` em ${listing.data.totals.months} meses` : ""}
        </p>
      )}

      {listing.isPending && <Skeleton rows={8} />}
      {listing.error && <ErrorState error={listing.error} onRetry={() => listing.refetch()} />}
      {!!items.length && listing.data && (
        <>
          <table>
            <thead>
              <tr>
                <th scope="col">data</th>
                {showUser && <th scope="col">usuário</th>}
                <th scope="col">descrição</th>
                <th scope="col" className="num">valor</th>
                <th scope="col">entidades</th>
                <th scope="col">marcas</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td className="mono" style={{ whiteSpace: "nowrap", fontSize: 12 }}>
                    {row.date}
                  </td>
                  {showUser && (
                    <td className="mono" style={{ fontSize: 12 }}>{row.short_id}</td>
                  )}
                  <td style={{ maxWidth: 380 }}>
                    <span className="mono" style={{ fontSize: 12 }}>
                      {row.description ?? "—"}
                    </span>
                  </td>
                  <td className="num">{cell(row.amount)}</td>
                  <td style={{ fontSize: 11.5, color: "var(--ink-3)", maxWidth: 240 }}>
                    {row.entities || "—"}
                  </td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    {row.is_chargeback && <span className="tag">estorno</span>}
                    {!row.realized && <span className="tag">agendado</span>}
                    {row.installment_total && (
                      <span className="tag">
                        {row.installment_current ?? "?"}/{row.installment_total}
                      </span>
                    )}
                    {row.flag && (
                      <span className={`tag ${row.flag.severity}`}
                            title={`${row.flag.rule} → ${row.flag.suggested_category ?? "?"}`}>
                        {row.flag.rule}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {listing.data.pages > 1 && (
            <div className="controls" style={{ marginTop: 14, justifyContent: "center" }}>
              <button className="btn" disabled={page <= 1}
                      onClick={() => setPage(page - 1)}>anterior</button>
              <span style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                {page} / {listing.data.pages}
              </span>
              <button className="btn" disabled={page >= listing.data.pages}
                      onClick={() => setPage(page + 1)}>próxima</button>
            </div>
          )}
        </>
      )}
      {listing.data && !items.length && (
        <p style={{ color: "var(--ink-3)", margin: 0 }}>Nenhum lançamento aqui.</p>
      )}
    </Card>
  );
}

/** Título do painel: `MERCADO · jun/26`, com o mês só quando há mês. */
export function entryTitle(category: string, month?: string | null) {
  return `Lançamentos de ${shortName(category)}${month ? ` · ${fmtMes(month, true)}` : ""}`;
}
